import { isEmpty } from 'lodash'
import queryString from 'query-string'

import { fetchingAPI, apiService } from '@/api'
import { apiErrorMessage } from '@/api/handleResponse'
import { toast } from '@/components/toasts/toast.helpers'
import { readBody } from '@/reducers/promptimizer/promptimizer.actions'
import { withoutLegacyModel } from '@/reducers/prompts/prompts.helpers'
import {
  setLoading,
  setPaginatedTasks,
  setFeedbackOptimizations,
  setOptimizationDetail,
  setOptimizationDetailLoading,
  setAgentAgenda,
  setAgentAgendaLoading,
  CALIBRATION_AGENT,
} from './promptFeedback.redux'
import { setCallExplorerData } from '../callSearch/callSearch.redux'
import { fetchTaskById } from '../tasks/tasks.actions'

// Convert expected result (yes/no/n/a) to score value (1/0/-1)
const EXPECTED_TO_SCORE = { yes: 1, no: 0, 'n/a': -1 }
const expectedResultToScore = (expectedResult) => EXPECTED_TO_SCORE[expectedResult] ?? null

export const submitPromptFeedback =
  ({
    promptId,
    promptName,
    callId,
    scorecardId,
    scorecardCriteriaScoreId,
    scorecardCriteriaScid,
    expectedResult,
    feedbackText,
    organizationId,
    organizationName,
    agentId,
    callType = 'call',
    criteriaUuid,
  }) =>
  async (dispatch, getState) => {
    dispatch(setLoading(true))

    try {
      const taskName = promptName
        ? `Prompt Feedback: ${promptName}`
        : `Prompt Feedback: Prompt ${promptId}`
      const body = {
        name: taskName,
        description: feedbackText,
        type: 'prompt_feedback',
        status: 'ready_for_review',
        prompt_id: promptId ? Number(promptId) : null,
        call_id: Number(callId),
        agent_id: Number(agentId),
        scorecard_criteria_scid: scorecardCriteriaScid || null,
        scorecard_criteria_score_id: scorecardCriteriaScoreId
          ? Number(scorecardCriteriaScoreId)
          : null,
        // Per-evidence feedback fields
        expected_result: expectedResult,
        user_feedback: feedbackText,
        metadata: {
          organization_name: organizationName,
          call_type: callType,
        },
      }

      // Submit the feedback task
      await fetchingAPI(
        `${apiService.workflow}/tasks?requested_organization_id=${organizationId}`,
        'POST',
        dispatch,
        JSON.stringify(body)
      )

      // Also update the scorecard criteria score to match the expected result
      if (scorecardId && criteriaUuid && expectedResult) {
        // Convert yes/no/n/a to score value (1/0/-1)
        const scoreValue = expectedResultToScore(expectedResult)

        if (scoreValue !== null) {
          // Format must be an array of criteria updates
          const updatedCriteria = [
            {
              scorecard_criteria_score_uuid: criteriaUuid,
              score: scoreValue,
              skipped: scoreValue === -1,
              note: '',
            },
          ]

          const params = queryString.stringify(
            { requested_organization_id: organizationId },
            { skipNull: true }
          )
          const url = `${apiService.scorecard}/scoring/scores/${scorecardId}/update_criteria?${params}`

          const updatedScorecard = await fetchingAPI(
            url,
            'POST',
            dispatch,
            JSON.stringify(updatedCriteria)
          )

          // Update the call explorer QA scores with the updated scorecard
          const { qaScores } = getState().callSearch.callExplorer
          if (qaScores?.length) {
            const updatedScores = qaScores.map((score) =>
              score.id === scorecardId ? updatedScorecard : score
            )
            dispatch(setCallExplorerData({ qaScores: updatedScores }))
          }
        }
      }
    } catch (err) {
      toast.error('Failed to submit feedback. Please try again.', { cause: err })
      throw err
    } finally {
      dispatch(setLoading(false))
    }
  }

export const fetchPromptFeedbackTasks =
  ({
    page = 1,
    limit = 8,
    sortBy = 'updated_at',
    sortOrder = 'desc',
    organizationId = null,
    scorecardConfigId = null,
    minReports = null,
    startDate = null,
    endDate = null,
    taskStatuses = null,
  } = {}) =>
  async (dispatch, getState) => {
    dispatch(setLoading(true))

    try {
      const { organizationid } = getState().currentUser
      const isBaltoAdmin = organizationid === 1

      const params = queryString.stringify(
        {
          task_types: 'prompt_feedback',
          page,
          limit,
          sort_by: sortBy,
          sort_order: sortOrder,
          all_orgs: isBaltoAdmin ? 'true' : undefined,
          organization_ids: organizationId || undefined,
          scorecard_config_ids: scorecardConfigId || undefined,
          min_evidence_count: minReports || undefined,
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          task_statuses: taskStatuses || undefined,
        },
        { skipNull: true, skipEmptyString: true }
      )

      const url = `${apiService.workflow}/tasks/paginated?${params}`
      const response = await fetchingAPI(url, 'GET', dispatch)

      dispatch(setPaginatedTasks(response))

      return response
    } catch (error) {
      toast.error('Failed to fetch prompt feedback tasks', { cause: error })
      return []
    } finally {
      dispatch(setLoading(false))
    }
  }

// Fetch the latest scorecard config with full relationship data (tags, users, etc.).
// The versions endpoint doesn't eager-load relationships, so we use it only to
// find the latest version ID, then fetch the full config by that ID.
const fetchLatestScorecardConfig = async (
  dispatch,
  scorecardConfigSid,
  scorecardConfigId,
  organizationId
) => {
  const orgParam = organizationId ? `?requested_organization_id=${organizationId}` : ''
  let sid = scorecardConfigSid

  if (!sid) {
    const config = await fetchingAPI(
      `${apiService.scorecard}/scoring/scorecards/scorecard/${scorecardConfigId}${orgParam}`,
      'GET',
      dispatch
    )
    sid = config?.sid
  }

  if (!sid) {
    throw new Error('Could not resolve scorecard config SID')
  }

  const versions = await fetchingAPI(
    `${apiService.scorecard}/scorecard_config/versions/${sid}${orgParam}`,
    'GET',
    dispatch
  )

  if (!versions?.length) {
    throw new Error('No scorecard config versions found')
  }

  // API doesn't guarantee order — sort descending by ID to get the latest
  const latestVersion = versions.sort((a, b) => b.id - a.id)[0]

  // Fetch the full config by ID to get complete relationship data (tags, users,
  // dispositions, voip_campaigns). The versions endpoint doesn't joinedload these.
  return fetchingAPI(
    `${apiService.scorecard}/scoring/scorecards/scorecard/${latestVersion.id}${orgParam}`,
    'GET',
    dispatch
  )
}

// Helper to create new prompt body from original + changes
const buildNewPromptBody = (originalPrompt, promptChanges, fullScorecardConfig, criteriaScid) => {
  // `promptChanges` carries the dropdown's llm_model_id and never a `model` name,
  // so carrying the original's name forward would pair a fresh id with the name of
  // the model it replaced. The test path already sends the id alone
  // (see usePromptTesting) — the save has to agree.
  const newPromptBody = withoutLegacyModel({
    ...originalPrompt,
    ...promptChanges,
    organization_id: fullScorecardConfig.organization_id,
    feature: 'qa_copilot',
    scid: criteriaScid,
  })
  delete newPromptBody.id
  delete newPromptBody.created_at
  delete newPromptBody.updated_at
  delete newPromptBody.prompt_tests
  return newPromptBody
}

// Helper to update criteria prompt_id in scorecard config
const updateScorecardConfigWithNewPrompt = (fullScorecardConfig, criteriaScid, newPromptId) => ({
  ...fullScorecardConfig,
  sections: fullScorecardConfig.sections.map((section) => ({
    ...section,
    measures: section.measures.map((measure) => ({
      ...measure,
      criteria: measure.criteria.map((criteria) =>
        criteria.scid === criteriaScid ? { ...criteria, prompt_id: newPromptId } : criteria
      ),
    })),
  })),
})

// Validate required data for applying prompt changes
const validatePromptChangeData = (
  promptChanges,
  scorecardConfigId,
  originalPrompt,
  criteriaScid
) => {
  const missingFields = []
  if (isEmpty(promptChanges)) {
    missingFields.push('prompt changes')
  }
  if (!scorecardConfigId) {
    missingFields.push('scorecard config ID')
  }
  if (!originalPrompt) {
    missingFields.push('original prompt')
  }
  if (!criteriaScid) {
    missingFields.push('criteria SCID')
  }
  return { canApply: isEmpty(missingFields), missingFields }
}

// Creates a new prompt version and points the criterion at it, without touching the task.
export const applyPromptChangesToScorecard =
  ({
    taskOrganizationId,
    promptChanges,
    originalPrompt,
    scorecardConfigId,
    scorecardConfigSid,
    criteriaScid,
  }) =>
  async (dispatch) => {
    const orgParam = taskOrganizationId ? `?requested_organization_id=${taskOrganizationId}` : ''

    const { canApply, missingFields } = validatePromptChangeData(
      promptChanges,
      scorecardConfigId,
      originalPrompt,
      criteriaScid
    )

    if (!canApply) {
      toast.error(`Unable to apply prompt changes: missing ${missingFields.join(', ')}`)
      throw new Error(`Missing required data: ${missingFields.join(', ')}`)
    }

    try {
      const fullScorecardConfig = await fetchLatestScorecardConfig(
        dispatch,
        scorecardConfigSid,
        scorecardConfigId,
        taskOrganizationId
      )

      const newPromptBody = buildNewPromptBody(
        originalPrompt,
        promptChanges,
        fullScorecardConfig,
        criteriaScid
      )

      const newPrompt = await fetchingAPI(
        `${apiService.summary}/prompts${orgParam}`,
        'POST',
        dispatch,
        JSON.stringify(newPromptBody)
      )

      const updatedScorecardConfig = updateScorecardConfigWithNewPrompt(
        fullScorecardConfig,
        criteriaScid,
        newPrompt.id
      )

      await fetchingAPI(
        `${apiService.scorecard}/scoring/scorecards/scorecard/${fullScorecardConfig.id}${orgParam}`,
        'PUT',
        dispatch,
        JSON.stringify(updatedScorecardConfig)
      )

      return newPrompt
    } catch (error) {
      toast.error('Failed to apply prompt changes to scorecard. Please try again.', {
        cause: error,
      })
      throw error
    }
  }

export const resolveWithPromptChanges = (params) => async (dispatch) => {
  const { taskId, taskOrganizationId } = params

  const newPrompt = await dispatch(applyPromptChangesToScorecard(params))

  // Mark task as completed (only after prompt changes succeeded)
  const patchUrl = taskOrganizationId
    ? `${apiService.workflow}/tasks/${taskId}?requested_organization_id=${taskOrganizationId}`
    : `${apiService.workflow}/tasks/${taskId}`
  await fetchingAPI(patchUrl, 'PATCH', dispatch, JSON.stringify({ status: 'completed' }))

  // Refresh both task states so inbox updates
  await Promise.all([
    dispatch(fetchTaskById(taskId, null, null, taskOrganizationId)),
    dispatch(fetchPromptFeedbackTasks()),
  ])

  toast.success('Prompt updated and task completed!')
  return newPrompt
}

// Flips a reported task to in_progress when an editor stages changes. Silent (no
// toast) so it doesn't compete with the "Changes staged" toast.
export const markTaskInProgress = (taskId, organizationId) => async (dispatch) => {
  const url = organizationId
    ? `${apiService.workflow}/tasks/${taskId}?requested_organization_id=${organizationId}`
    : `${apiService.workflow}/tasks/${taskId}`
  await fetchingAPI(url, 'PATCH', dispatch, JSON.stringify({ status: 'in_progress' }))
  await dispatch(fetchTaskById(taskId, null, null, organizationId))
}

// Applies the changes and refreshes the task, but leaves it open for further iteration.
export const savePromptChangesToScorecard = (params) => async (dispatch) => {
  const { taskId, taskOrganizationId } = params

  const newPrompt = await dispatch(applyPromptChangesToScorecard(params))

  await dispatch(fetchTaskById(taskId, null, null, taskOrganizationId))

  toast.success('Prompt saved to scorecard')
  return newPrompt
}

// The background optimizer's latest run per task, for the inbox card chips. Silent on failure —
// this is enrichment for admins/prompt editors; the inbox itself must never depend on it.
export const fetchFeedbackOptimizations = (taskIds) => async (dispatch) => {
  if (isEmpty(taskIds)) {
    return []
  }
  try {
    const response = await fetchingAPI(
      `${apiService.summary}/calibration/runs?${queryString.stringify({ task_ids: taskIds }, { arrayFormat: 'comma' })}`,
      'GET',
      dispatch
    )
    const data = await readBody(response)
    const optimizations = data?.optimizations ?? []
    dispatch(setFeedbackOptimizations(optimizations))
    return optimizations
  } catch (error) {
    return []
  }
}

// "Calibrate now": queue this task for the Calibration Agent (it starts right away), with optional
// Help-the-Agent guidance — stored on the run, never posted as a task comment.
export const requestCalibration =
  (taskId, guidance = null) =>
  async (dispatch) => {
    try {
      const response = await fetchingAPI(
        `${apiService.summary}/calibration/runs`,
        'POST',
        dispatch,
        JSON.stringify({ task_id: taskId, agent: CALIBRATION_AGENT, guidance })
      )
      const data = await readBody(response)
      if (data?.optimization_id) {
        dispatch(setFeedbackOptimizations([data]))
        toast.success('Sent to the Calibration Agent — it is starting now')
        return data
      }
      return null
    } catch (error) {
      // handleResponse has already toasted a server error; anything else is the backend's own words.
      if (!(error?.status >= 500)) {
        toast.error(apiErrorMessage(error, 'Could not send this task to the Calibration Agent'))
      }
      return null
    }
  }

export const fetchFeedbackOptimizationAgenda =
  (page = 1) =>
  async (dispatch) => {
    dispatch(setAgentAgendaLoading(true))
    try {
      const response = await fetchingAPI(
        `${apiService.summary}/calibration/agenda?${queryString.stringify({ page })}`,
        'GET',
        dispatch
      )
      const data = (await readBody(response)) ?? { items: [] }
      dispatch(setAgentAgenda(data.items ?? []))
      return data
    } catch (error) {
      return null
    } finally {
      dispatch(setAgentAgendaLoading(false))
    }
  }

const orgSettingsUrl = (orgId) =>
  `${apiService.summary}/calibration/agents/${CALIBRATION_AGENT}/orgs/${orgId}`

export const fetchCalibrationOrgSettings = (orgId) => async (dispatch) => {
  try {
    return await readBody(await fetchingAPI(orgSettingsUrl(orgId), 'GET', dispatch))
  } catch (error) {
    return null
  }
}

export const saveCalibrationOrgSettings = (orgId, settings) => async (dispatch) => {
  try {
    const response = await fetchingAPI(
      orgSettingsUrl(orgId),
      'PUT',
      dispatch,
      JSON.stringify(settings)
    )
    toast.success('Settings saved')
    return await readBody(response)
  } catch (error) {
    if (!(error?.status >= 500)) {
      toast.error('Failed to save settings', { cause: error })
    }
    return null
  }
}

export const fetchFeedbackOptimizationDetail = (optimizationId) => async (dispatch) => {
  dispatch(setOptimizationDetailLoading(true))
  try {
    const response = await fetchingAPI(
      `${apiService.summary}/calibration/runs/${optimizationId}`,
      'GET',
      dispatch
    )
    const data = await readBody(response)
    if (data?.optimization_id) {
      dispatch(setOptimizationDetail(data))
    }
    return data
  } catch (error) {
    return null
  } finally {
    dispatch(setOptimizationDetailLoading(false))
  }
}

// A person saved the agent's winner to the scorecard: record it as adopted. Silent — the save already
// succeeded, and a missed record only means the next run cannot say the fix was adopted.
export const recordCalibrationAdopted =
  ({ optimizationId, candidateId, newPromptId }) =>
  async (dispatch) => {
    try {
      await fetchingAPI(
        `${apiService.summary}/calibration/runs/${optimizationId}/adopted`,
        'POST',
        dispatch,
        JSON.stringify({ candidate_id: candidateId, new_prompt_id: newPromptId })
      )
      return await dispatch(fetchFeedbackOptimizationDetail(optimizationId))
    } catch (error) {
      return null
    }
  }

// Dismisses or restores reported calls on a task — one call or 150 in a single request. The
// evidence is hidden rather than deleted: the reporter's expected answer and reason stay on the
// rows, and the calibration loop stops reading the calls because both of its queries filter
// `NOT hidden`. dismissed: false puts them back.
export const setReportedCallsDismissed =
  ({ taskId, callIds, organizationId, dismissed }) =>
  async (dispatch) => {
    const url = organizationId
      ? `${apiService.workflow}/tasks/${taskId}/evidence/hidden?requested_organization_id=${organizationId}`
      : `${apiService.workflow}/tasks/${taskId}/evidence/hidden`

    await fetchingAPI(
      url,
      'PATCH',
      dispatch,
      JSON.stringify({ call_ids: callIds, hidden: dismissed })
    )
    // Refetch WITH hidden evidence so dismissed calls move to the drawer's Dismissed view
    // instead of vanishing from the payload.
    await dispatch(fetchTaskById(taskId, null, null, organizationId, true))

    const noun = callIds.length === 1 ? 'Call' : `${callIds.length} calls`
    toast.success(dismissed ? `${noun} dismissed` : `${noun} restored`)
  }
